import type { FieldError, FieldErrors, Resolver } from "react-hook-form";

import type { SpecItemRow } from "@/shared/models/SectionSpec";
import { validateAngleValue } from "@/shared/utils/angleValidation";
import {
	formatValidMeasurement,
	validateMeasurement,
} from "@/shared/utils/measurementPrecision";
import { isAngleSpecItem } from "@/shared/utils/sectionSpecUnits";

import { getAngleError, readAngleParts } from "../utils/SectionSpecAngle";
import { getSectionSpecCategoryTabs } from "./SectionSpecCategoryTabs";

export const SPEC_BAND_FIELD = {
	LTL: "Ltl",
	NOMINAL: "Nominal",
	UTL: "Utl",
} as const;

export type SpecBandField =
	(typeof SPEC_BAND_FIELD)[keyof typeof SPEC_BAND_FIELD];

export interface SectionSpecFormValues {
	printNo: string;
	area: string;
	target: string;
	min: string;
	max: string;
	legDelta: string;
}

interface SpecBandDraftValues {
	Ltl: string;
	Nominal: string;
	Utl: string;
}

export interface SpecItemDraftRow
	extends Omit<SpecItemRow, keyof SpecBandDraftValues>,
		SpecBandDraftValues {}

export interface SectionSpecDraftFormValues extends SectionSpecFormValues {
	items: SpecItemDraftRow[];
}

export const EMPTY_SECTION_SPEC_FORM: SectionSpecFormValues = {
	printNo: "",
	area: "",
	target: "",
	min: "",
	max: "",
	legDelta: "",
};

export const createEmptySectionSpecDraftForm =
	(): SectionSpecDraftFormValues => ({
		...EMPTY_SECTION_SPEC_FORM,
		items: [],
	});

// Decimal degrees retain the storage precision needed to represent whole minutes.
const ANGLE_DECIMAL_PATTERN = /^[+-]?(?:\d+(?:\.\d{0,4})?|\.\d{1,4})$/;
const ANGLE_DECIMAL_MESSAGE =
	"Enter an angle with up to 4 decimal places between 0° and 360°.";
const TARGET_BAND_MESSAGE =
	"Target value must be between Min and Max (inclusive).";
const TARGET_MIN_BAND_MESSAGE =
	"Target value must be greater than or equal to Min.";
const TARGET_MAX_BAND_MESSAGE =
	"Target value must be less than or equal to Max.";
const MIN_MAX_BAND_MESSAGE = "Min value must be less than or equal to Max.";
const MAX_MIN_BAND_MESSAGE = "Max value must be greater than or equal to Min.";
const MIN_BAND_MESSAGE = "Min value must be less than or equal to Target.";
const MAX_BAND_MESSAGE = "Max value must be greater than or equal to Target.";
const MIN_TARGET_MESSAGE =
	"Min lb/ft must be less than or equal to Target lb/ft";
const TARGET_MIN_MESSAGE =
	"Target lb/ft must be greater than or equal to Min lb/ft";
const TARGET_MAX_MESSAGE =
	"Target lb/ft must be less than or equal to Max lb/ft";
const MAX_TARGET_MESSAGE =
	"Max lb/ft must be greater than or equal to Target lb/ft";
const MIN_MAX_MESSAGE = "Min lb/ft must be less than or equal to Max lb/ft";
const MAX_MIN_MESSAGE = "Max lb/ft must be greater than or equal to Min lb/ft";

type WeightBandField = "min" | "target" | "max";

interface SpecBandErrors extends Partial<Record<SpecBandField, FieldError>> {}

const SPEC_BAND_FIELDS: readonly SpecBandField[] =
	Object.values(SPEC_BAND_FIELD);

const SPEC_BAND_LABELS: Record<SpecBandField, string> = {
	Ltl: "Min",
	Nominal: "Target",
	Utl: "Max",
};

const validationError = (message: string): FieldError => ({
	type: "validate",
	message,
});

export const parseOptionalSectionSpecDecimal = (raw: string): number | null => {
	const trimmed = raw.trim();
	return trimmed === "" ? null : Number(trimmed);
};

export interface SectionSpecValidationContext {
	initialValues?: SectionSpecFormValues;
	initialItems?: readonly SpecItemDraftRow[];
}

const getAngleBandError = (raw: string): string | undefined => {
	if (raw.trim() === "") return undefined;
	if (raw.includes("°")) return getAngleError(...readAngleParts(raw));
	return (
		validateAngleValue(Number(raw)) ??
		(ANGLE_DECIMAL_PATTERN.test(raw.trim()) ? undefined : ANGLE_DECIMAL_MESSAGE)
	);
};

const getDecimalError = (raw: string): string | undefined =>
	validateMeasurement(raw).message ?? undefined;

export const formatSectionSpecDimensionValue = (
	raw: string,
	row: Pick<SpecItemDraftRow, "Code" | "IsDegrees">,
) => {
	if (!isAngleSpecItem(row)) return formatValidMeasurement(raw);
	if (raw.trim() === "" || getAngleBandError(raw)) return raw;
	return Number(raw).toFixed(4);
};

const getBandErrors = (
	row: SpecItemDraftRow,
	initialRow?: SpecItemDraftRow,
): SpecBandErrors => {
	if (!row.Applies) return {};

	const errors: SpecBandErrors = {};
	// All scalar bands, including gauges without degrees, use three decimals.
	for (const field of SPEC_BAND_FIELDS) {
		const raw = row[field];
		const message = isAngleSpecItem(row)
			? getAngleBandError(raw)
			: getDecimalError(raw);
		if (message) errors[field] = validationError(message);
	}

	const ltl = errors.Ltl ? undefined : parseOptionalSectionSpecDecimal(row.Ltl);
	const nominal = errors.Nominal
		? undefined
		: parseOptionalSectionSpecDecimal(row.Nominal);
	const utl = errors.Utl ? undefined : parseOptionalSectionSpecDecimal(row.Utl);
	const setOrderError = (
		fields: readonly SpecBandField[],
		fallbackFields: readonly SpecBandField[],
		messages: Partial<Record<SpecBandField, string>>,
	) => {
		// Attribute a broken comparison to the edited endpoint, not its unchanged peer.
		const changedFields = initialRow
			? fields.filter(
					(field) =>
						parseOptionalSectionSpecDecimal(row[field]) !==
						parseOptionalSectionSpecDecimal(initialRow[field]),
				)
			: [];
		for (const field of changedFields.length === 1
			? changedFields
			: fallbackFields) {
			const message = messages[field];
			if (message) errors[field] ??= validationError(message);
		}
	};
	// Fix inverted limits first: no Target can satisfy an inverted interval.
	if (ltl != null && utl != null && ltl > utl) {
		setOrderError(["Ltl", "Utl"], ["Ltl", "Utl"], {
			Ltl: MIN_MAX_BAND_MESSAGE,
			Utl: MAX_MIN_BAND_MESSAGE,
		});
		return errors;
	}
	if (ltl != null && nominal != null && ltl > nominal) {
		setOrderError(["Ltl", "Nominal"], ["Nominal"], {
			Ltl: MIN_BAND_MESSAGE,
			Nominal: utl != null ? TARGET_BAND_MESSAGE : TARGET_MIN_BAND_MESSAGE,
		});
	}
	if (nominal != null && utl != null && nominal > utl) {
		setOrderError(["Nominal", "Utl"], ["Nominal"], {
			Nominal: ltl != null ? TARGET_BAND_MESSAGE : TARGET_MAX_BAND_MESSAGE,
			Utl: MAX_BAND_MESSAGE,
		});
	}
	return errors;
};

export const getSectionSpecItemError = (
	items: SpecItemDraftRow[],
	context: SectionSpecValidationContext = {},
): string | null => {
	const initialRows = new Map(
		context.initialItems?.map((row) => [row.ProfileItemId, row]),
	);
	const categoryLabels = new Map(
		getSectionSpecCategoryTabs(items).map((tab) => [tab.id, tab.label]),
	);
	const messages: string[] = [];
	for (const row of items) {
		const errors = getBandErrors(row, initialRows.get(row.ProfileItemId));
		for (const field of SPEC_BAND_FIELDS) {
			const message = errors[field]?.message;
			if (message) {
				messages.push(
					`${categoryLabels.get(row.ProfileCategoryId)} / ${row.Code} — ${SPEC_BAND_LABELS[field]} (${row[field]}): ${message}`,
				);
			}
		}
	}
	return messages.length > 0 ? messages.join("\n") : null;
};

export const sectionSpecDraftResolver: Resolver<
	SectionSpecDraftFormValues,
	SectionSpecValidationContext
> = (values, context) => {
	const errors: FieldErrors<SectionSpecDraftFormValues> = {};

	const decimalFields = [
		["area", values.area],
		["target", values.target],
		["min", values.min],
		["max", values.max],
		["legDelta", values.legDelta],
	] as const;
	for (const [field, value] of decimalFields) {
		const message = getDecimalError(value);
		if (message) errors[field] = validationError(message);
	}

	const min = parseOptionalSectionSpecDecimal(values.min);
	const target = parseOptionalSectionSpecDecimal(values.target);
	const max = parseOptionalSectionSpecDecimal(values.max);
	const minTargetOutOfOrder =
		!errors.min &&
		!errors.target &&
		min !== null &&
		target !== null &&
		min > target;
	const targetMaxOutOfOrder =
		!errors.target &&
		!errors.max &&
		target !== null &&
		max !== null &&
		target > max;
	const minMaxOutOfOrder =
		!errors.min && !errors.max && min !== null && max !== null && min > max;
	const setWeightOrderError = (
		fields: readonly WeightBandField[],
		fallbackField: WeightBandField,
		messages: Partial<Record<WeightBandField, string>>,
	) => {
		const initialValues = context?.initialValues;
		const changedFields = initialValues
			? fields.filter(
					(field) =>
						parseOptionalSectionSpecDecimal(values[field]) !==
						parseOptionalSectionSpecDecimal(initialValues[field]),
				)
			: [];
		// Keep comparison errors on the edited endpoint, as dimension bands do.
		const field = changedFields.length === 1 ? changedFields[0] : fallbackField;
		const message = messages[field];
		if (message) errors[field] ??= validationError(message);
	};
	if (minTargetOutOfOrder) {
		setWeightOrderError(["min", "target"], "min", {
			min: MIN_TARGET_MESSAGE,
			target: TARGET_MIN_MESSAGE,
		});
	}
	if (targetMaxOutOfOrder) {
		setWeightOrderError(["target", "max"], "target", {
			target: TARGET_MAX_MESSAGE,
			max: MAX_TARGET_MESSAGE,
		});
	}
	if (minMaxOutOfOrder) {
		setWeightOrderError(["min", "max"], errors.min ? "max" : "min", {
			min: MIN_MAX_MESSAGE,
			max: MAX_MIN_MESSAGE,
		});
	}

	const initialRows = new Map(
		context?.initialItems?.map((row) => [row.ProfileItemId, row]),
	);
	const itemErrors = values.items.map((row) =>
		getBandErrors(row, initialRows.get(row.ProfileItemId)),
	);
	if (itemErrors.some((rowErrors) => Object.keys(rowErrors).length > 0)) {
		errors.items =
			itemErrors as FieldErrors<SectionSpecDraftFormValues>["items"];
	}

	return Object.keys(errors).length === 0
		? { values, errors: {} }
		: { values: {}, errors };
};
